import React from 'react';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle2, Target, Award, TrendingUp } from 'lucide-react';
import { companyInfo } from '../data/mock';

const About = () => {
  return (
    <div className="min-h-screen pt-24">
      {/* Hero Section */}
      <section className="py-16 px-6 md:px-12 lg:px-24 bg-gradient-to-br from-maroon-50 to-white">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-gray-900 mb-6">
            About <span className="text-maroon-600">Maroons</span>
          </h1>
          <p className="text-xl text-gray-600 leading-relaxed">
            {companyInfo.description}
          </p>
        </div>
      </section>

      {/* Mission & Vision */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <Card className="border-none shadow-xl">
              <CardContent className="p-10">
                <div className="h-16 w-16 bg-maroon-100 rounded-2xl flex items-center justify-center mb-6">
                  <Target className="h-8 w-8 text-maroon-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Mission</h2>
                <p className="text-gray-600 leading-relaxed">
                  To empower businesses of all sizes with comprehensive, professional solutions that drive growth, efficiency, and success. We believe every enterprise deserves access to expert services that transform challenges into opportunities.
                </p>
              </CardContent>
            </Card>

            <Card className="border-none shadow-xl">
              <CardContent className="p-10">
                <div className="h-16 w-16 bg-maroon-100 rounded-2xl flex items-center justify-center mb-6">
                  <Award className="h-8 w-8 text-maroon-600" />
                </div>
                <h2 className="text-3xl font-bold text-gray-900 mb-4">Our Vision</h2>
                <p className="text-gray-600 leading-relaxed">
                  To be the most trusted and innovative business solutions partner, recognized for delivering exceptional value, maintaining unwavering integrity, and fostering long-term partnerships that fuel sustainable business growth.
                </p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      {/* Our Values */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-gray-50">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Our Core <span className="text-maroon-600">Values</span>
            </h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              These principles guide every decision we make and every service we deliver.
            </p>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {[
              {
                title: 'Excellence',
                desc: 'We pursue the highest standards in everything we do, from initial consultation to final delivery.'
              },
              {
                title: 'Integrity',
                desc: 'Transparency, honesty, and confidentiality form the foundation of our client relationships.'
              },
              {
                title: 'Innovation',
                desc: 'We stay ahead of industry trends, adopting the latest tools and methodologies to serve you better.'
              },
              {
                title: 'Partnership',
                desc: 'Your success is our success. We work collaboratively to achieve your business goals.'
              }
            ].map((value, idx) => (
              <Card key={idx} className="border-none shadow-lg hover:shadow-xl transition-all duration-300">
                <CardContent className="p-8 text-center">
                  <div className="h-14 w-14 bg-gradient-to-br from-maroon-500 to-maroon-700 rounded-full flex items-center justify-center mx-auto mb-6">
                    <CheckCircle2 className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                  <p className="text-gray-600 text-sm">{value.desc}</p>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose Us */}
      <section className="py-20 px-6 md:px-12 lg:px-24">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900 mb-4">
              Why Businesses <span className="text-maroon-600">Trust Us</span>
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: TrendingUp,
                title: 'Proven Track Record',
                desc: 'Years of experience delivering results across industries and business sizes.'
              },
              {
                icon: Award,
                title: 'Expert Team',
                desc: 'Industry specialists with deep expertise in their respective fields.'
              },
              {
                icon: CheckCircle2,
                title: 'Comprehensive Solutions',
                desc: 'Everything you need under one roof, eliminating the hassle of multiple vendors.'
              }
            ].map((item, idx) => (
              <div key={idx} className="text-center">
                <div className="h-20 w-20 bg-maroon-100 rounded-2xl flex items-center justify-center mx-auto mb-6">
                  <item.icon className="h-10 w-10 text-maroon-600" />
                </div>
                <h3 className="text-2xl font-bold text-gray-900 mb-3">{item.title}</h3>
                <p className="text-gray-600">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Philosophy */}
      <section className="py-20 px-6 md:px-12 lg:px-24 bg-gradient-to-r from-maroon-600 to-maroon-800">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-bold text-white mb-6">
            "Everything Gets Solved Here"
          </h2>
          <p className="text-xl text-maroon-100 leading-relaxed">
            This isn't just our motto—it's our promise. When you partner with Maroons, you gain a dedicated team committed to solving your business challenges with expertise, creativity, and unwavering support. We handle the complexity so you can focus on what matters most: growing your business and serving your customers.
          </p>
        </div>
      </section>
    </div>
  );
};

export default About;